import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import ForcePasswordModal from './components/ForcePasswordModal';
import ProtectedRoute from './components/ProtectedRoute';
import { MapsProvider } from './components/MapsProvider';
import { useAuthStore } from './store/authStore';
import HomePage from './pages/HomePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage';
import CategoriesPage from './pages/CategoriesPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import ReportIssuePage from './pages/ReportIssuePage';
import ReturnsPage from './pages/ReturnsPage';
import ScrollToTop from './components/ScrollToTop';

/* ── Layout wrapper ── */
const AppLayout: React.FC = () => (
  <>
    <Navbar />
    <main style={{ minHeight: 'calc(100vh - 160px)' }}>
      <Outlet />
    </main>
    <Footer />
    <AuthModal />
    <ForcePasswordModal />
  </>
);

/* ── Placeholder for pages you'll build later ── */
const Placeholder: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '80px 24px', textAlign: 'center' }}>
    <h1 style={{ fontSize: '2rem', color: 'var(--secondary)' }}>{title}</h1>
    <p style={{ color: 'var(--gray-500)', marginTop: 8 }}>This page is coming soon.</p>
  </div>
);

/* ── Helper to wrap protected pages ── */
const Protected: React.FC<{ title: string }> = ({ title }) => (
  <ProtectedRoute>
    <Placeholder title={title} />
  </ProtectedRoute>
);

const App: React.FC = () => {
  // Revalidate any persisted session against the backend on boot. The store
  // hydrates the user from localStorage immediately for instant UI; this
  // call refreshes it from /users/me + /customers/me if a token is present.
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
  <MapsProvider>
  <Router>
    <ScrollToTop />
    <Routes>
      <Route element={<AppLayout />}>
        {/* Public */}
        <Route path="/" element={<HomePage />} />

        {/* Protected — requires login */}
        <Route path="/products" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
        <Route path="/products/:slug" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
        <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/order-confirmation" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
        <Route path="/wishlist" element={<Protected title="Wishlist" />} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
        <Route path="/report-issue" element={<ProtectedRoute><ReportIssuePage /></ProtectedRoute>} />
        <Route path="/returns" element={<ProtectedRoute><ReturnsPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<Placeholder title="404 — Page Not Found" />} />
      </Route>
    </Routes>
  </Router>
  </MapsProvider>
  );
};

export default App;
